import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Document } from 'react-pdf';
import { parseSlideRef, buildSlideRef } from '../../../types/live';
import {
  DeckFile,
  PageCounts,
  deckTotal,
  deckGlobalIndex,
  deckNext,
  deckPrev,
  deckFileOf,
} from '../../../types/deck';
import DocumentViewer, { authedFile } from '../../shared/DocumentViewer';
import LiveBadge from './LiveBadge';

interface SlideStageProps {
  slideRef: string | null;
  // Ordered combined deck (Unit 10b). The active slide_ref may point to a file
  // outside this deck (tolerant), in which case it is still displayed ad hoc.
  deckFiles: DeckFile[];
  // All switchable PDF materials for this meeting. Drives the controller's
  // "switch document" picker so the presenter can jump to any meeting PDF
  // mid-session, not just the pre-picked deck. Defaults to deckFiles when omitted.
  documents?: DeckFile[];
  isLive: boolean;
  reconnecting?: boolean;
  // UTC ISO instant the session went live; drives the LIVE badge elapsed timer.
  startedAt?: string | null;
  // Duration (s) of the most recent completed session; frozen final time shown
  // on the badge once not live.
  lastSessionSeconds?: number | null;
  // Fallback section label when the current file's name is unknown.
  fallbackLabel?: string | null;
  // Secretary/controller only: navigate the deck (drives PUT live/slide upstream).
  canControl: boolean;
  // Whether the viewer is the controller regardless of live state, used to pick
  // role-aware empty/waiting copy (M2). canControl gates navigation and is only
  // true while also live.
  isController?: boolean;
  // Follower affordance: allow a non-controller to browse the deck on their own
  // (local-only — never calls onRequestPage, so it does not touch the live
  // session or other viewers). They can snap back to the presenter at any time.
  allowIndependentBrowse?: boolean;

  // Materials load state, so the placeholder distinguishes loading from an empty
  // deck and can offer Retry on failure (M2).
  materialsLoading?: boolean;
  materialsError?: boolean;
  // Count of assistant context items (insights + questions) for the slide on
  // stage; shown as a ✦ chip on the topbar linking the stage to the assistant.
  contextCount?: number;
  onRetryMaterials?: () => void;
  onRequestPage?: (fileId: string, page: number) => void;
  // Reports where an independently-browsing follower currently is: their local
  // slide_ref, or null whenever they are following the presenter again (or the
  // affordance is off). The parent uses this to refresh the follower's own
  // insights for the slide they settle on (Unit 15c) — it is debounced there,
  // so this fires on every step. Must be referentially stable upstream.
  onBrowseSlideChange?: (slideRef: string | null) => void;
  // Reports the per-file page counts as react-pdf discovers them, so the
  // parent can share the deck's global slide numbering (e.g. with the
  // assistant panel's slide labels). Must be referentially stable upstream.
  onPageCountsChange?: (counts: PageCounts) => void;
}

// Renders the current slide of a combined multi-file PDF deck (slide_ref
// "{fileId}:{page}") with a LIVE badge, the current file's section label, a
// global "Slide X of N" across the whole deck, and Prev/Next that cross file
// boundaries. The controller drives navigation (PUT live/slide); members follow
// slide changes pushed via the live stream. Page counts load asynchronously, so
// counters/navigation fill in as each file reports its numPages.
// Memoized: the parent (LiveMeeting) re-renders on every SSE event during a
// live session (transcript segments, interim mic results, insights, questions).
// Without this, each parent re-render re-renders the react-pdf Page and repaints
// the canvas, which the user sees as the slide flickering. The memo holds only
// because every prop is referentially stable (deckFiles/sectionLabel via
// useMemo, callbacks via useCallback upstream).
const SlideStageComponent: React.FC<SlideStageProps> = ({
  slideRef,
  deckFiles,
  documents,
  isLive,
  reconnecting,
  startedAt,
  lastSessionSeconds,
  fallbackLabel,
  canControl,
  isController,
  allowIndependentBrowse,
  materialsLoading,
  materialsError,
  contextCount,
  onRetryMaterials,
  onRequestPage,
  onBrowseSlideChange,
  onPageCountsChange,
}) => {
  // Follower independent-browse state. following=true (default) tracks the
  // presenter's slide_ref; when a follower navigates on their own, following
  // flips false and browseRef holds their local position. Snapping back to the
  // presenter clears browseRef and re-enables following. Controllers ignore this
  // entirely (they always drive the shared slide_ref).
  const [following, setFollowing] = useState(true);
  const [browseRef, setBrowseRef] = useState<string | null>(null);

  // A follower is browsing independently only when the affordance is enabled and
  // they have chosen to stop following. Everyone else (controller, or a follower
  // who is following) sees the shared presenter slide_ref.
  const browsing = !canControl && !!allowIndependentBrowse && !following;
  const effectiveRef = browsing ? (browseRef ?? slideRef) : slideRef;

  // If the affordance turns off (e.g. session ends), resume following so the
  // view can never get stuck on a stale local position.
  useEffect(() => {
    if (!allowIndependentBrowse && (!following || browseRef !== null)) {
      setFollowing(true);
      setBrowseRef(null);
    }
  }, [allowIndependentBrowse, following, browseRef]);

  // Publish the independent-browse position upstream. Reporting null while
  // following means snapping back to the presenter cancels any pending refresh
  // rather than requesting another one.
  useEffect(() => {
    onBrowseSlideChange?.(browsing ? effectiveRef : null);
  }, [browsing, effectiveRef, onBrowseSlideChange]);

  const pos = useMemo(() => {
    const parsed = parseSlideRef(effectiveRef);
    return parsed ? { fileId: parsed.fileId, page: parsed.page } : null;
  }, [effectiveRef]);

  const [counts, setCounts] = useState<PageCounts>({});

  // Referentially stable: DocumentViewer is memoized, so an inline arrow here
  // would defeat it.
  const setCount = useCallback(
    (fileId: string, n: number) =>
      setCounts((prev) => (prev[fileId] === n ? prev : { ...prev, [fileId]: n })),
    []
  );

  // Share discovered page counts with the parent so other components (the
  // assistant panel's slide labels) can use the same global numbering.
  useEffect(() => {
    onPageCountsChange?.(counts);
  }, [counts, onPageCountsChange]);

  // Hidden count-loaders for every deck file except the one currently visible
  // (its count comes from the DocumentViewer below), so the combined deck's
  // total and cross-file navigation resolve as files load.
  const loaderFiles = useMemo(
    () => deckFiles.filter((f) => f.fileId !== pos?.fileId),
    [deckFiles, pos?.fileId]
  );

  // Memoize the react-pdf `file` objects so their reference is stable across
  // re-renders (see the authedFile note in DocumentViewer). Without this,
  // react-pdf refetches every loader file on any parent re-render.
  const loaderFileObjs = useMemo(
    () => loaderFiles.map((f) => ({ fileId: f.fileId, file: authedFile(f.fileId) })),
    [loaderFiles]
  );

  const total = deckTotal(deckFiles, counts);
  const globalIndex = pos ? deckGlobalIndex(deckFiles, counts, pos) : null;
  const nextPos = pos ? deckNext(deckFiles, counts, pos) : null;
  const prevPos = pos ? deckPrev(deckFiles, counts, pos) : null;

  // Defense-in-depth for the go-live seed: when a controller is live on a deck
  // with no active slide yet (active_slide_ref null), let "Next →" present the
  // first slide so a manual path exists even if the auto-seed effect fails.
  // Previous stays disabled (there is no position to step back from).
  // Whether this viewer may step the deck: the controller always, or a follower
  // with the independent-browse affordance. Followers never drive the shared
  // slide_ref — their navigation is local (see goTo).
  const canBrowseIndependently = !canControl && !!allowIndependentBrowse && deckFiles.length > 0;

  const nextTarget =
    nextPos ||
    // With no active slide yet, let "Next →" open the first slide: the controller
    // seeds the shared position; a browse-capable follower starts browsing.
    (((canControl && isLive) || canBrowseIndependently) && !pos && deckFiles.length > 0
      ? { fileId: deckFiles[0].fileId, page: 1 }
      : null);
  const canNavigate = canControl || canBrowseIndependently;

  // Unified navigation. The controller broadcasts via onRequestPage; a follower
  // updates only their own local position (and implicitly stops following).
  const goTo = (fileId: string, page: number) => {
    if (canControl) {
      onRequestPage?.(fileId, page);
      return;
    }
    setFollowing(false);
    setBrowseRef(buildSlideRef(fileId, page));
  };

  const returnToPresenter = () => {
    setFollowing(true);
    setBrowseRef(null);
  };
  // The document picker lists every switchable PDF (falls back to the deck).
  // Include the current file even if it is not in the list (an off-deck ad-hoc
  // slide_ref) so the select always reflects what is on screen.
  const pickerDocs = useMemo(() => {
    const base = documents && documents.length > 0 ? documents : deckFiles;
    if (pos && !base.some((d) => d.fileId === pos.fileId)) {
      return [...base, deckFileOf(deckFiles, pos.fileId) || { fileId: pos.fileId }];
    }
    return base;
  }, [documents, deckFiles, pos]);

  const currentFile =
    (pos ? deckFileOf(deckFiles, pos.fileId) : null) ||
    (pos ? pickerDocs.find((d) => d.fileId === pos.fileId) || null : null);
  const sectionLabel = currentFile?.filename || fallbackLabel || 'Presentation';
  // Show the picker when the viewer can navigate (controller, or a follower
  // browsing on their own) and there is more than one document to choose from.
  const showDocPicker = (canControl || canBrowseIndependently) && pickerDocs.length > 1;

  let counterText = 'Slide —';
  if (pos) {
    if (globalIndex != null && total.complete) counterText = `Slide ${globalIndex} of ${total.total}`;
    else if (globalIndex != null) counterText = `Slide ${globalIndex}…`;
    else counterText = `Slide ${pos.page}…`;
  }
  // Announce the file alongside the position for screen-reader users; the
  // visible counter stays compact (the section label is shown separately).
  const counterAnnouncement = pos ? `${counterText}, ${sectionLabel}` : counterText;

  const hasDeck = deckFiles.length > 0;

  // Role-aware empty/waiting/ended copy for when no slide is being shown (M2):
  // pre-live vs waiting-for-presenter vs ended vs no-deck are distinct states.
  const renderPlaceholder = () => {
    if (materialsLoading) {
      return <div className="slide-stage-placeholder">Loading materials…</div>;
    }
    if (materialsError) {
      return (
        <div className="slide-stage-placeholder">
          <div>Couldn’t load the meeting materials.</div>
          {onRetryMaterials && (
            <button className="meeting-btn meeting-btn-secondary meeting-btn-sm" onClick={onRetryMaterials}>
              Retry
            </button>
          )}
        </div>
      );
    }
    if (slideRef && !pos) {
      return (
        <div className="slide-stage-placeholder">
          Slide reference “{slideRef}” is not a document page yet.
        </div>
      );
    }
    if (!hasDeck) {
      return (
        <div className="slide-stage-placeholder">
          {isController
            ? 'No PDF materials in this deck — add materials in the workspace.'
            : 'No presentation materials are available for this meeting.'}
        </div>
      );
    }
    if (isController) {
      return (
        <div className="slide-stage-placeholder">
          {isLive ? 'Choose a slide to present.' : 'Ready to present — start Live.'}
        </div>
      );
    }
    return (
      <div className="slide-stage-placeholder">
        {isLive
          ? canBrowseIndependently
            ? 'The presenter has not selected a slide yet. Use Next → to browse the pack on your own.'
            : 'The presenter has not selected a slide yet.'
          : 'Waiting for the presenter to start.'}
      </div>
    );
  };

  return (
    <div className="slide-stage">
      <div className="slide-stage-topbar">
        {showDocPicker ? (
          <label className="slide-stage-doc-picker">
            <span className="sr-only">Presented document</span>
            <select
              className="slide-stage-doc-select"
              value={pos?.fileId ?? ''}
              onChange={(e) => {
                const fileId = e.target.value;
                if (fileId && fileId !== pos?.fileId) goTo(fileId, 1);
              }}
              aria-label="Switch presented document"
            >
              {!pos && <option value="" disabled>Choose a document…</option>}
              {pickerDocs.map((d, i) => (
                <option key={d.fileId} value={d.fileId}>
                  {d.filename || `Document ${i + 1}`}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <div className="slide-stage-section" title={sectionLabel}>{sectionLabel}</div>
        )}
        {typeof contextCount === 'number' && contextCount > 0 && (
          <span
            className="slide-stage-context-chip"
            title={`${contextCount} insight${contextCount === 1 ? '' : 's'} & question${contextCount === 1 ? '' : 's'} for this slide`}
            aria-label={`${contextCount} context item${contextCount === 1 ? '' : 's'} for this slide`}
          >
            ✦ {contextCount}
          </span>
        )}
        <div className="slide-stage-controls">
          {canNavigate ? (
            <>
              <button
                className="meeting-btn meeting-btn-secondary meeting-btn-sm"
                disabled={!prevPos}
                onClick={() => prevPos && goTo(prevPos.fileId, prevPos.page)}
                aria-label="Previous slide"
              >
                ← Previous
              </button>
              <span className="slide-stage-counter" role="status" aria-live="polite" aria-label={counterAnnouncement}>{counterText}</span>
              <button
                className="meeting-btn meeting-btn-secondary meeting-btn-sm"
                disabled={!nextTarget}
                onClick={() => nextTarget && goTo(nextTarget.fileId, nextTarget.page)}
                aria-label="Next slide"
              >
                Next →
              </button>
              {/* Follower browsing on their own: show status + a way back. */}
              {canBrowseIndependently && (
                browsing ? (
                  <button
                    className="meeting-btn meeting-btn-primary meeting-btn-sm slide-stage-follow-btn"
                    onClick={returnToPresenter}
                    aria-label="Return to the presenter's slide"
                  >
                    ↩ Follow presenter
                  </button>
                ) : (
                  <span className="slide-stage-following">Following presenter</span>
                )
              )}
            </>
          ) : (
            <>
              <span className="slide-stage-counter" role="status" aria-live="polite" aria-label={counterAnnouncement}>{counterText}</span>
              <span className="slide-stage-following">Following presenter</span>
            </>
          )}
        </div>
        <LiveBadge
          isLive={isLive}
          reconnecting={reconnecting}
          startedAt={startedAt}
          lastSessionSeconds={lastSessionSeconds}
        />
      </div>

      {/* The rendering core is shared with the prep-materials reader. The
          placeholder is only built when there is no slide on stage, so it stays
          a stable null while presenting and cannot defeat the viewer's memo. */}
      <DocumentViewer
        className="slide-stage-canvas"
        placeholderClassName="slide-stage-placeholder"
        fileId={pos?.fileId ?? null}
        page={pos?.page ?? 1}
        onNumPages={setCount}
        placeholder={pos ? null : renderPlaceholder()}
        loadingLabel="Loading slide…"
        errorLabel="Failed to load slide."
      />

      {/* Off-screen loaders: read each other deck file's page count only. */}
      <div className="slide-stage-loaders" aria-hidden="true">
        {loaderFileObjs.map((f) => (
          <Document
            key={f.fileId}
            file={f.file}
            onLoadSuccess={({ numPages: n }) => setCount(f.fileId, n)}
            loading={<span />}
            error={<span />}
          />
        ))}
      </div>
    </div>
  );
};

const SlideStage = React.memo(SlideStageComponent);

export default SlideStage;
